import { makeAutoObservable, runInAction } from 'mobx';
import { loginCodeApi } from '@/services/loginCodeApi';
import type {
  AccountLoginCodeStatus,
  AccountLoginCode as ApiAccountLoginCode,
  AccountLoginCodeSettings as ApiAccountLoginCodeSettings,
  CreateAccountLoginCodeRequest,
  UpdateAccountLoginCodeExpiryRequest,
} from '@/types';
import { normalizeAccountLoginCodeStatus } from '@/types';

export interface AccountLoginCode {
  id: string;
  appId: string;
  userId: string;
  displayName: string;
  codeMasked: string;
  status: AccountLoginCodeStatus;
  expiresAt?: string;
  lastUsedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  plainCode?: string;
}

export interface AccountLoginCodeSettings {
  appId: string;
  enabled: boolean;
  updatedBy: string;
  updatedAt?: string;
}

function transformLoginCode(item: ApiAccountLoginCode): AccountLoginCode {
  return {
    id: item.id,
    appId: item.app_id,
    userId: item.user_id,
    displayName: item.display_name,
    codeMasked: item.code_masked,
    status: normalizeAccountLoginCodeStatus(item.status),
    expiresAt: item.expires_at,
    lastUsedAt: item.last_used_at,
    createdBy: item.created_by,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    plainCode: item.plain_code,
  };
}

function transformSettings(
  item: ApiAccountLoginCodeSettings,
): AccountLoginCodeSettings {
  return {
    appId: item.app_id,
    enabled: item.enabled,
    updatedBy: item.updated_by,
    updatedAt: item.updated_at,
  };
}

class LoginCodeStore {
  loginCodes: AccountLoginCode[] = [];
  settings: AccountLoginCodeSettings | null = null;
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  searchQuery = '';
  statusFilter: AccountLoginCodeStatus | undefined = undefined;
  loading = false;
  settingsLoading = false;
  creating = false;
  updating = false;
  deleting = false;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchLoginCodes(appId: string) {
    if (!appId) {
      runInAction(() => {
        this.loginCodes = [];
        this.pagination.total = 0;
      });
      return;
    }

    this.loading = true;
    try {
      const response = await loginCodeApi.listAccountLoginCodes({
        app_id: appId,
        page_num: this.pagination.page,
        page_size: this.pagination.pageSize,
        query: this.searchQuery || undefined,
        status: this.statusFilter,
      });
      runInAction(() => {
        this.loginCodes = response.login_codes.map(transformLoginCode);
        this.pagination.total = response.total;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async fetchSettings(appId: string) {
    if (!appId) {
      runInAction(() => {
        this.settings = null;
      });
      return;
    }

    this.settingsLoading = true;
    try {
      const response = await loginCodeApi.getAccountLoginCodeSettings({
        app_id: appId,
      });
      runInAction(() => {
        this.settings = response.settings
          ? transformSettings(response.settings)
          : null;
        this.settingsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.settingsLoading = false;
      });
      throw error;
    }
  }

  async createLoginCode(data: CreateAccountLoginCodeRequest) {
    this.creating = true;
    try {
      const response = await loginCodeApi.createAccountLoginCode(data);
      await this.fetchLoginCodes(data.app_id);
      return transformLoginCode(response.login_code);
    } finally {
      runInAction(() => {
        this.creating = false;
      });
    }
  }

  async updateLoginCodeExpiry(
    appId: string,
    data: UpdateAccountLoginCodeExpiryRequest,
  ) {
    this.updating = true;
    try {
      const response = await loginCodeApi.updateAccountLoginCodeExpiry(data);
      await this.fetchLoginCodes(appId);
      return transformLoginCode(response.login_code);
    } finally {
      runInAction(() => {
        this.updating = false;
      });
    }
  }

  async deleteLoginCode(appId: string, id: string, deletedBy?: string) {
    this.deleting = true;
    try {
      await loginCodeApi.deleteAccountLoginCode({ id, deleted_by: deletedBy });
      await this.fetchLoginCodes(appId);
    } finally {
      runInAction(() => {
        this.deleting = false;
      });
    }
  }

  async updateSettings(appId: string, enabled: boolean, updatedBy?: string) {
    this.settingsLoading = true;
    try {
      const response = await loginCodeApi.updateAccountLoginCodeSettings({
        app_id: appId,
        enabled,
        updated_by: updatedBy,
      });
      runInAction(() => {
        this.settings = transformSettings(response.settings);
        this.settingsLoading = false;
      });
      return this.settings;
    } catch (error) {
      runInAction(() => {
        this.settingsLoading = false;
      });
      throw error;
    }
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
    this.pagination.page = 1;
  }

  setStatusFilter(status: AccountLoginCodeStatus | undefined) {
    this.statusFilter = status;
    this.pagination.page = 1;
  }

  setPagination(page: number, pageSize?: number) {
    this.pagination.page = page;
    if (pageSize) {
      this.pagination.pageSize = pageSize;
    }
  }
}

export const loginCodeStore = new LoginCodeStore();
